import { BotMessageSquare } from "lucide-react";
import * as React from "react";

import { AgentThreadSidebar } from "@/AgentThread/index";
import {
  CollaborationBarStorage,
  useCollaborationBar,
} from "@/shared/state/collaborationBar";
import { CollaborationMode } from "@/shared/state/collaborationBar/CollaborationBarContext";

import styles from "./CollaborationBar.module.css";
import { ModeButton } from "./ModeButton";
import { ResizeHandle } from "./ResizeHandle";

interface CollaborationBarContentProps {
  close: () => void;
  activeMode: CollaborationMode | null;
}

const CollaborationBarContent = ({
  activeMode,
  close,
}: CollaborationBarContentProps) => {
  if (!activeMode) {
    return null;
  }

  switch (activeMode) {
    case "ai-agent-chat":
      return (
        <div className={styles.contentPlaceholder}>
          <AgentThreadSidebar onClose={close} />
        </div>
      );
    default:
      return null;
  }
};

/**
 * CollaborationBar component - a right-side sidebar for AI threads and collaboration features.
 *
 * Collapsed state: 40px wide with mode buttons (AI Chat, Comments)
 * Expanded state: Resizable width with mode-specific content area
 */
export function CollaborationBar() {
  const collaborationBar = useCollaborationBar();
  const contentRef = React.useRef<HTMLDivElement>(null);

  const onResize = React.useCallback((deltaX: number) => {
    if (!contentRef.current) {
      return;
    }

    const currentWidth = contentRef.current.offsetWidth;
    const newWidth = currentWidth + deltaX;

    contentRef.current.style.width = `${newWidth}px`;
    CollaborationBarStorage.setWidth(newWidth);
  }, []);

  // Initialize width from localStorage when expanded
  React.useEffect(
    function initializeWidthFromLocalStorage() {
      if (!contentRef.current || !collaborationBar.isOpen) {
        return;
      }

      const savedWidth = CollaborationBarStorage.getWidth();
      if (savedWidth !== null) {
        // The value is gated by minWidth and maxWidth in CSS
        contentRef.current.style.width = `${savedWidth}px`;
      }
    },
    [collaborationBar.isOpen],
  );

  const handleModeClick = React.useCallback(
    (mode: CollaborationMode) => {
      if (collaborationBar.isOpen && collaborationBar.activeMode === mode) {
        collaborationBar.close();
      } else {
        collaborationBar.setActiveMode(mode);
      }
    },
    [collaborationBar],
  );

  return (
    <div className={styles.collaborationBar}>
      {/* Collapsed bar with mode buttons - always visible */}
      <div className={styles.collapsedBar}>
        <ModeButton
          isActive={collaborationBar.activeMode === "ai-agent-chat"}
          onClick={() => handleModeClick("ai-agent-chat")}
        >
          <div className={styles.textAndIcon}>
            <BotMessageSquare size={20} />
            AI Chat
          </div>
        </ModeButton>
      </div>

      {/* Expanded content - only visible when open */}
      {collaborationBar.isOpen && (
        <div ref={contentRef} className={styles.expandedContent}>
          <ResizeHandle onResize={onResize} />
          <CollaborationBarContent
            activeMode={collaborationBar.activeMode}
            close={collaborationBar.close}
          />
        </div>
      )}
    </div>
  );
}
